import { useState, useEffect } from 'react';
import { subscriptionService } from '../services/subscriptionService';
import { STATUS } from '../constants';
import { locationUtils } from '../utils/locationUtils';

/**
 * Custom hook for managing subscription-related operations
 */
export const useSubscriptions = () => {
  const [plans, setPlans] = useState([]);
  const [monthlyPlans, setMonthlyPlans] = useState([]);
  const [yearlyPlans, setYearlyPlans] = useState([]);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [error, setError] = useState(null);
  const [userCountry, setUserCountry] = useState(null);
  const [billingCycle, setBillingCycle] = useState('Monthly');
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Check if we're in signup flow (has plan parameter)
  const isInSignupFlow = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.has('plan');
    }
    return false;
  };

  // Load subscription plans with billing cycle support
  const loadPlans = async (cycle = billingCycle) => {
    if (status === STATUS.LOADING) return;
    
    console.log('useSubscriptions: Loading plans for cycle:', cycle);
    
    setStatus(STATUS.LOADING);
    setError(null);
    
    // Detect user country first
    const countryData = await locationUtils.detectUserCountry();
    setUserCountry(countryData);
    
    const result = await subscriptionService.getPlans(cycle);
    
    console.log('useSubscriptions: API result:', result);
    
    if (result.success) {
      console.log('useSubscriptions: Setting plans:', result.data);
      setPlans(result.data);
      setBillingCycle(cycle);
      
      // Store plans by cycle for savings calculation
      if (cycle === 'Monthly') {
        setMonthlyPlans(result.data);
      } else {
        setYearlyPlans(result.data);
      }
      
      setStatus(STATUS.SUCCESS);
    } else {
      setError(result.error);
      setStatus(STATUS.ERROR);
    }
    
    return result;
  };

  // Switch billing cycle
  const switchBillingCycle = async (cycle) => {
    if (cycle !== 'Monthly' && cycle !== 'Yearly') {
      console.error('Invalid billing cycle:', cycle);
      return;
    }
    await loadPlans(cycle);
  };

  // Auto-load plans on hook initialization
  useEffect(() => {
    if (status === STATUS.IDLE && !hasInitialized && !isInSignupFlow()) {
      console.log('useSubscriptions: Auto-loading plans for initialization');
      // Load both monthly and yearly plans for savings calculation
      const loadAllPlans = async () => {
        setHasInitialized(true);
        await loadPlans('Monthly');
        await loadPlans('Yearly');
        // Set back to monthly as default
        await loadPlans('Monthly');
      };
      loadAllPlans();
    } else if (isInSignupFlow()) {
      console.log('useSubscriptions: In signup flow, skipping auto-loading');
      setHasInitialized(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- init runs once via hasInitialized/status gate
  }, [hasInitialized, status]);

  // Get plan by ID (now uses plan_prices.id)
  const getPlanById = (planId) => {
    return plans.find(plan => plan.id === planId) || null;
  };

  // Get plan by subscription plan ID (for backward compatibility)
  const getPlanBySubscriptionId = (subscriptionPlanId) => {
    return plans.find(plan => plan.plan_id === subscriptionPlanId) || null;
  };

  // Get corresponding plan in different billing cycle for savings calculation
  const getCorrespondingPlan = (currentPlan, targetCycle) => {
    if (targetCycle === 'Monthly') {
      return monthlyPlans.find(plan => plan.plan_id === currentPlan.plan_id);
    } else {
      return yearlyPlans.find(plan => plan.plan_id === currentPlan.plan_id);
    }
  };

  // Assign subscription to client
  const assignSubscription = async (subscriptionData) => {
    const result = await subscriptionService.assign(subscriptionData);
    return result;
  };

  // Get client subscription
  const getClientSubscription = async (clientId) => {
    const result = await subscriptionService.getClientSubscription(clientId);
    return result;
  };

  // Validate user limit
  const validateUserLimit = async (clientId, productId) => {
    const result = await subscriptionService.validateUserLimit(clientId, productId);
    return result;
  };

  // Validate token limit
  const validateTokenLimit = async (clientId, requestedTokens = 0) => {
    const result = await subscriptionService.validateTokenLimit(clientId, requestedTokens);
    return result;
  };

  return {
    // State
    plans,
    monthlyPlans,
    yearlyPlans,
    isLoading: status === STATUS.LOADING,
    isError: status === STATUS.ERROR,
    error,
    userCountry,
    billingCycle,
    
    // Actions
    loadPlans,
    switchBillingCycle,
    getPlanById,
    getPlanBySubscriptionId,
    getCorrespondingPlan,
    assignSubscription,
    getClientSubscription,
    validateUserLimit,
    validateTokenLimit
  };
}; 